/**
 * STORY-304: export of an incident card answer — no new dependency.
 *
 * - Markdown: the answer's own text as a Blob, through the chart export's
 *   download helper. The file name is the time the card was made only —
 *   never a problem title, host or tenant name.
 * - PDF: the browser's print dialog ("Save as PDF"). While the dialog is
 *   open, the body carries PRINT_BODY_CLASS and a copy of the card's content
 *   element, marked PRINT_ATTR="active", is the body's last child; the
 *   @media print rule in styles/index.css takes every other child of the
 *   body out of the layout (display: none) and prints only the copy. A copy
 *   at the top of the body, not the card in place: hidden in place, the
 *   earlier messages still took their height and printed as blank pages
 *   before the card (review-304 r2). The class and the copy are removed
 *   when printing ends, so printing any other page is unaffected.
 */
import { downloadBlob } from './chartExport';

export const PRINT_BODY_CLASS = 'printing-incident-card';
export const PRINT_ATTR = 'data-print-card';

/**
 * The instant the card was made — its marker's `generated_at` — so a card
 * downloaded later from the chat history is named for when its data was
 * fetched, not for the click. Falls back to `now` when the marker has none.
 */
export function cardGeneratedAt(generatedAt: string | null | undefined, now: Date = new Date()): Date {
  const when = generatedAt ? new Date(generatedAt) : null;
  return when && !Number.isNaN(when.getTime()) ? when : now;
}

/** incident-card-<yyyyMMdd-HHmm>.md, in UTC like the card's own «generated at» line. */
export function incidentCardFilename(when: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const day = `${when.getUTCFullYear()}${p(when.getUTCMonth() + 1)}${p(when.getUTCDate())}`;
  return `incident-card-${day}-${p(when.getUTCHours())}${p(when.getUTCMinutes())}.md`;
}

/** Save `content` as a Markdown file; returns the file name used. */
export function downloadIncidentCard(content: string, when: Date = new Date()): string {
  const name = incidentCardFilename(when);
  downloadBlob(new Blob([content], { type: 'text/markdown;charset=utf-8' }), name);
  return name;
}

/** Open the print dialog showing only `target` (printed from a copy). */
export function printIncidentCard(target: HTMLElement): void {
  const body = document.body;
  body.querySelectorAll(`:scope > [${PRINT_ATTR}]`).forEach((stale) => stale.remove());
  const copy = target.cloneNode(true) as HTMLElement;
  copy.setAttribute(PRINT_ATTR, 'active');
  const cleanup = () => {
    body.classList.remove(PRINT_BODY_CLASS);
    copy.remove();
    window.removeEventListener('afterprint', cleanup);
    window.removeEventListener('pointerdown', cleanup, true);
    window.removeEventListener('keydown', cleanup, true);
  };
  body.appendChild(copy);
  body.classList.add(PRINT_BODY_CLASS);
  // afterprint ends it. The next click or key press is the fallback for a
  // browser that never fires afterprint, so the marks cannot outlive the
  // dialog and restrict a later, ordinary print of the page.
  window.addEventListener('afterprint', cleanup);
  window.addEventListener('pointerdown', cleanup, true);
  window.addEventListener('keydown', cleanup, true);
  window.print();
}
